<template>
  <div
    ref="inputWithDropdown"
    class="InputWithDropdown"
  >
    <div class="min-h-48 border border-light relative z-above">
      <div
        class="px-12 flex items-center w-full min-h-[46px]
                type-sm leading-single
                select-none
                transition-all"
        @click="setOpen"
      >
        <img
          alt="search"
          src="/icons/search.svg"
          class="basis-20 shrink-0 h-20 w-20 mr-8 z-above relative self-start mt-12"
        >
        <!-- input -->
        <input
          v-show="open || selectedOptions.length === 0"
          :id="componentId"
          ref="searchInput"
          v-model="input"
          type="text"
          :placeholder="placeholder"
          class="leading-single basis-full"
          @focus="open = true"
        >

        <!-- selected options -->
        <div
          v-if="!open"
        >
          <div
            v-for="selected in selectedLabels"
            :key="selected.id"
            class="type-sm inline-flex items-center whitespace-nowrap mr-8 leading-single py-2"
          >
            <span class="mr-2">{{ selected.label }}</span>
            <img
              v-if="selected.label"
              alt="close"
              src="/icons/close.svg"
              class="relative h-16 w-16 ml-auto duration-300 cursor-pointer"
              @click.prevent.stop="onDelete(selected.id)"
            >
          </div>
        </div>
      </div>

      <!-- options dropdown -->
      <div
        v-if="open"
        class="absolute top-[46px] -left-1 w-[calc(100%+2px)]
      bg-lightest border border-light"
      >
        <div
          v-for="item in filteredItems"
          :key="item.value"
          class="px-12 py-8 type-sm-medium hover:bg-lighter cursor-pointer flex items-center content-start"
          :class="{
            'bg-lighter': selectedOptions.includes(item.value),
          }"
          @click="onSelect(item.value)"
        >
          <div class="content-start flex">
            <input
              type="checkbox"
              class="whiteCheck darkestChecked"
              :checked="selectedOptions.includes(item.value)"
            >
          </div>
          <div class="flex items-center cursor-pointer">
            <span class="ml-12">{{ item.label }}</span>
            <span class="type-xs-medium text-dark ml-8">#{{ item.value }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { onClickOutside } from '@vueuse/core';

const componentId = useId();

type Options = { value: string | number; label: string, disabled?: boolean }

const props = defineProps<{
  items: Options[],
  placeholder: string;
  selectedPlaceholder: string; // shows instad of placeholder when something is selected
  defaultValue?: Array<string | number>;
  prefix?: string;
  multiSelect?: boolean;
}>();

const emit = defineEmits<{
  (e: 'select', input: Array<string | number>): void;
  (e: 'close'): void;
}>();

const selectedOptions = ref<Array<string | number>>([]);
const open = ref(false);
const input = ref('');
const inputWithDropdown = ref();

const searchInput = ref<HTMLElement>();

const setOpen = () => {
  open.value = true;

};

watch(open, (newVal) => {
  if (newVal) {
    nextTick(()=> searchInput.value?.focus());
  }
});

const close = () => {
  open.value = false;
  emit('close');
};

onMounted(() => {
  selectedOptions.value = props.defaultValue || [];
});

const selectedLabels = computed(() => {
  //return props.items.filter(item => selectedOptions.value.includes(item.value)).map(item => (item.label));

  return selectedOptions.value.map((i) => {
    return {
      id: i,
      label: props.items.find(f => f.value === i)?.label || '',
    };
  });

});

const filteredItems = computed(() => {
  const regex = new RegExp(input.value, 'i');
  return props.items.filter(item => {
    return regex.test(item.label) || regex.test(item.value.toString());
  });
});

const onSelect = (item: string | number) => {
  // deselect
  if (selectedOptions.value.includes(item)) {
    selectedOptions.value = selectedOptions.value.filter(f => f !== item);
  } else {
    if (props.multiSelect) {
      selectedOptions.value.push(item);
    } else {
      selectedOptions.value = [ item ];
    }
  }

  emit('select', selectedOptions.value);

  if (!props.multiSelect) {
    close();
  }
};

const onDelete = (item: string | number) => {
  selectedOptions.value = selectedOptions.value.filter(f => f !== item);
  emit('select', selectedOptions.value);
};

onClickOutside(inputWithDropdown, () => {
  close();
});

watch(() => props.defaultValue, () => {
  if (props.defaultValue) {
    selectedOptions.value = props.defaultValue;
  }
});
</script>
